import {Component, inject, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {Land} from "../../../models/land/Land";
import {PFLICHTFELDMARKER} from "../../../../assets/constants/constants";
import {StudentTicketMandateHolderDto} from "../../../models/student-ticket/payment/StudentTicketMandateHolderDto";
import {ValidatorService} from "angular-iban";
import {InputComponent} from "../../../shared/form/input/input.component";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {SelectComponent} from "../../../shared/form/select/select.component";
import {PlzValidationHelperService} from "../../../@core/utils/forms/plz-validation-helper.service";
import {CheckboxComponent} from "../../../shared/form/checkbox/checkbox.component";
import {KeyString} from "../../../models/textbaustein/KeyString";
import {AbstractFormModalComponent} from "../../../shared/modal/abstract-form-modal.component";
import {ModalShellComponent} from "../../../shared/modal/modal-shell/modal-shell.component";

@Component({
  selector: 'app-mandate-holder-modal',
  templateUrl: './mandate-holder-modal.component.html',
  styleUrls: ['./mandate-holder-modal.component.scss'],
  imports: [
    InputComponent,
    TranslatePipe,
    ReactiveFormsModule,
    SelectComponent,
    CheckboxComponent,
    ModalShellComponent
  ],
  standalone: true
})
export class MandateHolderModalComponent extends AbstractFormModalComponent implements OnInit, OnChanges {

  @ViewChildren(InputComponent) protected inputComponents: QueryList<InputComponent>;
  @ViewChildren(SelectComponent) protected selectComponents: QueryList<SelectComponent>;
  @ViewChildren(CheckboxComponent) protected checkboxComponents: QueryList<CheckboxComponent>;

  @Input() initialValues: StudentTicketMandateHolderDto;

  protected form: UntypedFormGroup | null = null;
  protected validationErrorKey = 'TICKET_PAYMENT_INFORMATION';

  countryList = [];
  formReady = false;
  bicRequiredMarker = '';

  private readonly fb = inject(UntypedFormBuilder);
  private readonly translate = inject(TranslateService);
  readonly plzHelper = inject(PlzValidationHelperService);

  ngOnInit(): void {
    this.initCountryList();
    this.initForm();
    this.form?.get('mandateHolderIban')?.valueChanges.subscribe((iban: string) => {
      this.markBicRequired(iban);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initializeValues();
  }

  initCountryList(): void {
    this.countryList = Object.keys(Land).map((name) => {
      return {name, value: Land[name as keyof typeof Land]};
    });
  }

  initForm(): void {
    this.form = this.fb.group({
      mandateHolderFirstname: [null, Validators.required],
      mandateHolderLastname: [undefined, Validators.required],
      mandateHolderDateOfBirth: [undefined, Validators.required],
      mandateHolderStreet: ['', Validators.required],
      mandateHolderAddressAddition: [undefined],
      mandateHolderPostalCode: [undefined, Validators.required],
      mandateHolderCity: [undefined, Validators.required],
      mandateHolderCountry: [undefined, Validators.required],
      mandateHolderIban: [undefined, [Validators.required, ValidatorService.validateIban]],
      mandateHolderBankName: [undefined, Validators.required],
      mandateHolderBic: [undefined],
      mandateHolderSepaMandateAccepted: [false, Validators.requiredTrue]
    });
    this.initializeValues();
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  initializeValues(): void {
    if (!this.form) return;
    this.form.get('mandateHolderFirstname').patchValue(this.initialValues?.mandateHolderFirstname);
    this.form.get('mandateHolderLastname').patchValue(this.initialValues?.mandateHolderLastname);
    this.form.get('mandateHolderDateOfBirth').patchValue(this.initialValues?.mandateHolderDateOfBirth);
    this.form.get('mandateHolderStreet').patchValue(this.initialValues?.mandateHolderStreet);
    this.form.get('mandateHolderAddressAddition').patchValue(this.initialValues?.mandateHolderAddressAddition);
    this.form.get('mandateHolderPostalCode').patchValue(this.initialValues?.mandateHolderPostalCode);
    this.form.get('mandateHolderCity').patchValue(this.initialValues?.mandateHolderCity);
    const country = this.countryList.find(c => c.name === this.initialValues?.mandateHolderCountry);
    this.form.get('mandateHolderCountry').patchValue(country?.name);
    this.formReady = true;
  }

  markBicRequired(ibanString: string): void {
    if (!ibanString ||
      (ibanString?.length === 1 && ibanString?.toUpperCase().startsWith("D")) ||
      (ibanString?.length >= 2 && ibanString?.toUpperCase().startsWith("DE"))) {
      this.form.get('mandateHolderBic')?.removeValidators([Validators.required]);
      this.form.get('mandateHolderBic')?.updateValueAndValidity();
      this.bicRequiredMarker = '';
    } else {
      this.form.get('mandateHolderBic')?.addValidators([Validators.required]);
      this.form.get('mandateHolderBic')?.updateValueAndValidity();
      this.bicRequiredMarker = PFLICHTFELDMARKER;
    }
  }

  override save(): void {
    this.form.markAllAsTouched();
    if (this.form && this.form.valid) {
      this.activeModal.close(this.getMandateHolderDto());
    } else {
      let entityNameTranslated: string = this.translate.instant('COMPOSITE-MESSAGE.DERDIEDAS.' + 'TICKET_PAYMENT_INFORMATION');
      let errorMessage: string = entityNameTranslated + this.translate.instant('COMPOSITE-MESSAGE.UNVOLLSTAENDIG_PLURAL');
      this.customToastService.showError(errorMessage + ' Es fehlt: ' + this.getNotValid());
    }
  }

  getNotValid(): string {
    let notValid = '';
    const controls = this.form.controls;
    this.inputComponents.forEach(component => {
      const controlName = component.id;
      if (controls[controlName] && !controls[controlName].valid) {
        notValid += component.getTitle().replace(PFLICHTFELDMARKER, '') + ', ';
      }
    });
    this.selectComponents.forEach(component => {
      const controlName = component.id;
      if (controls[controlName] && !controls[controlName].valid) {
        notValid += component.getTitle().replace(PFLICHTFELDMARKER, '') + ', ';
      }
    });
    this.checkboxComponents.forEach(component => {
      const controlName = component.id;
      if (controls[controlName] && !controls[controlName].valid) {
        notValid += component.getTitle().replace(PFLICHTFELDMARKER, '') + ', ';
      }
    });
    return notValid.substring(0, notValid.length - 2);
  }

  protected getCloseValue(): unknown {
    return this.getMandateHolderDto();
  }

  getMandateHolderDto(): StudentTicketMandateHolderDto {
    const formValue = this.form.value;
    return {
      mandateHolderFirstname: formValue.mandateHolderFirstname,
      mandateHolderLastname: formValue.mandateHolderLastname,
      mandateHolderDateOfBirth: formValue.mandateHolderDateOfBirth,
      mandateHolderStreet: formValue.mandateHolderStreet,
      mandateHolderAddressAddition: formValue.mandateHolderAddressAddition,
      mandateHolderPostalCode: formValue.mandateHolderPostalCode,
      mandateHolderCity: formValue.mandateHolderCity,
      mandateHolderCountry: formValue.mandateHolderCountry,
      mandateHolderBankName: formValue.mandateHolderBankName,
      mandateHolderIban: formValue.mandateHolderIban,
      mandateHolderBic: formValue.mandateHolderBic,
      mandateHolderSepaMandateAccepted: formValue.mandateHolderSepaMandateAccepted
    };
  }

  setPlzPatternValidator(plzControlName: string, landControlName: string): void {
    const land = this.getFormItem(landControlName)?.value;
    if (land) {
      this.getFormItem(plzControlName)?.setValidators([
        Validators.maxLength(this.plzHelper.getMaxPlzLength(land)),
        Validators.pattern(this.plzHelper.getPlzPattern(land))
      ]);
    } else {
      this.getFormItem(plzControlName)?.clearValidators();
    }
    this.getFormItem(plzControlName)?.updateValueAndValidity();
  }

  getMaxPlzLength(landControlName:string): number {
    return this.plzHelper.getMaxPlzLength(this.getFormItem(landControlName)?.value);
  }
  getPlzPattern(landControlName:string): string {
    return this.plzHelper.getPlzPattern(this.getFormItem(landControlName)?.value);
  }

  getFormItem(s: string) {
    return this.form?.get(s);
  }

  protected readonly PFLICHTFELDMARKER = PFLICHTFELDMARKER;
  protected readonly KeyString = KeyString;
}
